import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import {
  Banner,
  Button,
  Checkbox,
  ConfirmDialog,
  ErrorState,
  PageHead,
  Skeleton,
  TextArea,
  TextField,
  focusFirstInvalid,
  useToast,
} from "@ui/react";
import { AlertIcon, ChevronLeftIcon, PlusIcon, TrashIcon } from "@ui/react/icons";
import {
  createService,
  listServices,
  updateService,
  type ServiceInput,
} from "../../lib/api/contractor";
import type { ServiceVariant } from "../../lib/types/models";
import { errorMessage } from "../../lib/errorMessage";

/* Payouts settle to a South African bank account, so every price is in rands.
 * The field is carried rather than edited, because a currency the Paystack
 * subaccount cannot receive would be a price nobody can pay. */
const DEFAULT_CURRENCY = "ZAR";

interface VariantDraft {
  key: string;
  /** Preserved on edit so the server keeps the id customers' bookings point at. */
  variantId?: string;
  label: string;
  durationMinutes: string;
  priceAmount: string;
  priceCurrency: string;
  enabled: boolean;
}

function blankVariant(key: string): VariantDraft {
  return {
    key,
    label: "",
    durationMinutes: "60",
    priceAmount: "",
    priceCurrency: DEFAULT_CURRENCY,
    enabled: true,
  };
}

function draftFromVariant(variant: ServiceVariant, key: string): VariantDraft {
  return {
    key,
    ...(variant.variantId ? { variantId: variant.variantId } : {}),
    label: variant.label,
    durationMinutes: String(variant.durationMinutes),
    priceAmount: String(variant.priceAmount),
    priceCurrency: variant.priceCurrency || DEFAULT_CURRENCY,
    enabled: variant.enabled !== false,
  };
}

const ADD_OPTION_ID = "add-option-button";

export function ServiceEditorScreen() {
  const toast = useToast();
  const { serviceId = "" } = useParams();
  const navigate = useNavigate();

  // The mobile app's convention, kept so a link built by either client works.
  const isCreate = serviceId === "new";

  const formRef = useRef<HTMLFormElement>(null);
  const keySeq = useRef(0);
  const nextKey = useCallback(() => `variant-${keySeq.current++}`, []);

  const [loadState, setLoadState] = useState<{ loading: boolean; error: unknown }>({
    loading: !isCreate,
    error: null,
  });
  const [reloadKey, setReloadKey] = useState(0);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [displayOrder, setDisplayOrder] = useState("0");
  const [enabled, setEnabled] = useState(true);
  const [variants, setVariants] = useState<VariantDraft[]>(() => [blankVariant("variant-init")]);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  /* A four-option service is sixteen typed fields, and the only exit at the top
     of this screen reads "All services" — a breadcrumb, not a warning. A ref
     rather than state: nothing renders differently while the form is dirty, so
     a re-render per keystroke would buy nothing. The form's own onChange
     catches every field (React's onChange IS the input event, and it bubbles);
     adding and removing options are the two mutations that are not one.
     Cancel is deliberately NOT guarded — discarding is what Cancel means. */
  const dirty = useRef(false);
  const [confirmLeave, setConfirmLeave] = useState(false);

  useEffect(() => {
    if (isCreate) return;

    let ignore = false;
    setLoadState({ loading: true, error: null });

    // There is no GET /profile/services/:id — the list is the only read, which
    // is what the mobile editor does too.
    listServices()
      .then((services) => {
        if (ignore) return;
        const service = services.find((candidate) => candidate.serviceId === serviceId);
        if (!service) {
          setLoadState({ loading: false, error: new Error("That service no longer exists.") });
          return;
        }
        setName(service.name);
        setDescription(service.description ?? "");
        setDisplayOrder(String(service.displayOrder ?? 0));
        setEnabled(service.enabled);
        setVariants(
          service.variants && service.variants.length > 0
            ? service.variants.map((variant) => draftFromVariant(variant, nextKey()))
            : [blankVariant(nextKey())],
        );
        setLoadState({ loading: false, error: null });
      })
      .catch((error: unknown) => {
        if (!ignore) setLoadState({ loading: false, error });
      });

    return () => {
      ignore = true;
    };
  }, [isCreate, serviceId, reloadKey, nextKey]);

  const patchVariant = (key: string, patch: Partial<VariantDraft>) => {
    setVariants((current) =>
      current.map((variant) => (variant.key === key ? { ...variant, ...patch } : variant)),
    );
  };

  const validate = (): boolean => {
    const next: Record<string, string> = {};

    if (!name.trim()) next["name"] = "Give this service a name.";

    const order = Number(displayOrder);
    if (!Number.isInteger(order) || order < 0) {
      next["displayOrder"] = "Use a whole number, 0 or more.";
    }

    if (variants.length === 0) {
      next["variants"] = "Add at least one option so customers have something to book.";
    }

    variants.forEach((variant) => {
      if (!variant.label.trim()) next[`${variant.key}-label`] = "Name this option.";

      const minutes = Number(variant.durationMinutes);
      if (!Number.isInteger(minutes) || minutes < 1) {
        next[`${variant.key}-duration`] = "At least 1 minute.";
      }

      const price = Number(variant.priceAmount);
      // The API binds priceAmount as `required,min=0`, and Go's `required`
      // rejects the zero value — so a free option is a 400, not a free option.
      if (!Number.isFinite(price) || price <= 0) {
        next[`${variant.key}-price`] = "Enter a price above R0.";
      }
    });

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSave = async () => {
    if (!validate()) {
      focusFirstInvalid(formRef.current);
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      // PATCH REPLACES THE VARIANTS LIST WHOLESALE. Every option must be in
      // this array on every save — sending only the edited one would silently
      // delete the rest of the contractor's prices.
      const body: ServiceInput = {
        name: name.trim(),
        description: description.trim(),
        enabled,
        displayOrder: Number(displayOrder),
        variants: variants.map((variant) => ({
          ...(variant.variantId ? { variantId: variant.variantId } : {}),
          label: variant.label.trim(),
          durationMinutes: Number(variant.durationMinutes),
          priceAmount: Number(variant.priceAmount),
          priceCurrency: variant.priceCurrency || DEFAULT_CURRENCY,
          enabled: variant.enabled,
        })),
      };

      if (isCreate) {
        await createService(body);
      } else {
        await updateService(serviceId, body);
      }
      toast.show(isCreate ? "Service created." : "Service saved.");
      navigate("/settings/services");
    } catch (error) {
      setSaveError(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };


  return (
    <div className="stack stack--loose">
      <PageHead
        eyebrow="Booking setup"
        title={isCreate ? "New service" : "Edit service"}
        back={
          <Link
            className="back-link"
            to="/settings/services"
            onClick={(event) => {
              if (!dirty.current) return;
              event.preventDefault();
              setConfirmLeave(true);
            }}
          >
            <ChevronLeftIcon />
            All services
          </Link>
        }
      />

      {loadState.loading ? (
        <div role="status">
          <span className="sr-only">Loading this service</span>
          <Skeleton height="22rem" />
        </div>
      ) : loadState.error ? (
        <ErrorState
          message={errorMessage(loadState.error)}
          onRetry={() => setReloadKey((key) => key + 1)}
        />
      ) : (
        <>
          {saveError ? (
            <Banner tone="danger" title="We couldn't save this">
              {saveError}
            </Banner>
          ) : null}

          <form
            ref={formRef}
            className="form"
            noValidate
            onChange={() => {
              dirty.current = true;
            }}
            onSubmit={(event) => {
              event.preventDefault();
              void onSave();
            }}
          >
            <div className="card card--dense">
              <div className="stack">
                <TextField
                  label="Service name"
                  required
                  value={name}
                  error={errors["name"] ?? null}
                  hint="What a customer would call it — e.g. Geyser service."
                  onChange={(event) => setName(event.target.value)}
                />
                <TextArea
                  label="Description"
                  value={description}
                  hint="Optional. What's included, what isn't."
                  onChange={(event) => setDescription(event.target.value)}
                />
                <div className="field-grid">
                  <TextField
                    label="Display order"
                    type="number"
                    min={0}
                    step={1}
                    value={displayOrder}
                    error={errors["displayOrder"] ?? null}
                    hint="Lower numbers show first."
                    onChange={(event) => setDisplayOrder(event.target.value)}
                  />
                </div>
                <Checkbox
                  label="Customers can book this service"
                  checked={enabled}
                  onChange={(event) => setEnabled(event.target.checked)}
                />
              </div>
            </div>

            <div className="stack">
              <div className="cluster cluster--between">
                <h2 className="headline-sm">Options</h2>
                <Button
                  id={ADD_OPTION_ID}
                  variant="tertiary"
                  onClick={() => {
                    dirty.current = true;
                    setVariants((current) => [...current, blankVariant(nextKey())]);
                  }}
                >
                  <PlusIcon />
                  Add option
                </Button>
              </div>
              <p className="muted">
                Each option is a version of this service a customer can pick, with its own
                duration and price.
              </p>
              {errors["variants"] ? (
                <p className="field__error">
                  <AlertIcon />
                  {errors["variants"]}
                </p>
              ) : null}

              {variants.map((variant, index) => (
                <div key={variant.key} className="card card--dense card--raised">
                  <div className="stack">
                    <div className="cluster cluster--between">
                      <span className="label-md">Option {index + 1}</span>
                      {variants.length > 1 ? (
                        <button
                          type="button"
                          className="icon-btn"
                          aria-label={`Remove option ${index + 1}`}
                          onClick={() => {
                            dirty.current = true;
                            setVariants((current) =>
                              current.filter((candidate) => candidate.key !== variant.key),
                            );
                            // This button unmounts with its own row, and focus
                            // would otherwise fall to <body>.
                            requestAnimationFrame(() =>
                              document.getElementById(ADD_OPTION_ID)?.focus(),
                            );
                          }}
                        >
                          <TrashIcon />
                        </button>
                      ) : null}
                    </div>

                    <TextField
                      label="Option name"
                      required
                      value={variant.label}
                      error={errors[`${variant.key}-label`] ?? null}
                      onChange={(event) => patchVariant(variant.key, { label: event.target.value })}
                    />

                    <div className="field-grid">
                      <TextField
                        label="Duration (minutes)"
                        type="number"
                        min={1}
                        step={5}
                        required
                        value={variant.durationMinutes}
                        error={errors[`${variant.key}-duration`] ?? null}
                        hint="How long the slot blocks out."
                        onChange={(event) =>
                          patchVariant(variant.key, { durationMinutes: event.target.value })
                        }
                      />
                      <TextField
                        label="Price (R)"
                        type="number"
                        min={1}
                        step={10}
                        required
                        value={variant.priceAmount}
                        error={errors[`${variant.key}-price`] ?? null}
                        hint="In rands, what the customer pays."
                        onChange={(event) =>
                          patchVariant(variant.key, { priceAmount: event.target.value })
                        }
                      />
                    </div>

                    <Checkbox
                      label="Customers can book this option"
                      checked={variant.enabled}
                      onChange={(event) =>
                        patchVariant(variant.key, { enabled: event.target.checked })
                      }
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="cluster">
              <Button type="submit" loading={saving}>
                {isCreate ? "Create service" : "Save changes"}
              </Button>
              <Link className="btn btn-secondary" to="/settings/services">
                Cancel
              </Link>
            </div>
          </form>
        </>
      )}

      {confirmLeave ? (
        <ConfirmDialog
          title="Leave without saving?"
          confirmLabel="Discard changes"
          cancelLabel="Keep editing"
          destructive
          onCancel={() => setConfirmLeave(false)}
          onConfirm={() => navigate("/settings/services")}
          body={
            <p>
              Nothing on this screen has been saved yet. Leaving now throws away everything
              you've typed — prices, durations and all.
            </p>
          }
        />
      ) : null}
    </div>
  );
}
